/**
 * Admin Dashboard entity KPI UI
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Tooltip,
  Box,
  Stack,
  Typography,
  Avatar,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import CreditCardOffOutlinedIcon from '@mui/icons-material/CreditCardOffOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ApartmentIcon from '@mui/icons-material/Apartment';
import API from '../../api';
import { HttpErrorHandler, ErrorUI } from '../../utils/ErrorHandler';
import { LoadingUI } from '../../utils/LayoutStyles';
import { fNumber } from '../../utils/formatNumber';
import { CustomTextField } from '../../utils/StyledInputs';
import { EntitiesDialog } from '../../utils/EntitiesDialog';
import { DefaultOutlinedButton } from '../../utils/Buttons';
import Chart, { useChart } from '../../components/chart';

const KPI_CONFIG = [
  {
    key: 'descendants_total',
    label: 'Entità sottostanti',
    icon: AccountTreeOutlinedIcon,
    color: 'primary',
    target: 'children',
  },
  {
    key: 'users_total',
    label: 'Utenti totali',
    icon: PeopleAltOutlinedIcon,
    color: 'info',
    target: 'users',
    category: '',
  },
  {
    key: 'current_campaign_members',
    label: 'Iscritti',
    icon: HowToRegOutlinedIcon,
    color: 'purple',
    target: 'users',
    category: 'member',
  },
  {
    key: 'unverified_users',
    label: 'Non verificati',
    icon: GppMaybeOutlinedIcon,
    color: 'warning',
    target: 'users',
    category: 'unverified',
  },
  {
    key: 'external_positions_users',
    label: 'Con incarico istituzionale',
    icon: PublicOutlinedIcon,
    color: 'success',
    target: 'users',
    category: 'external',
  },
  {
    key: 'internal_positions_users',
    label: 'Con incarico politico',
    icon: BusinessCenterOutlinedIcon,
    color: 'blue',
    target: 'users',
    category: 'internal',
  },
  {
    key: 'lapsed_members',
    label: 'Tessera non rinnovata',
    icon: CreditCardOffOutlinedIcon,
    color: 'error',
    target: 'users',
    category: 'expired',
  },
];

function KpiCard({ label, value, color, Icon, onClick }) {
  const theme = useTheme();
  const paletteColor = theme.palette[color] ?? theme.palette.primary;

  return (
    <Card
      onClick={onClick}
      sx={{
        p: 2.5,
        height: '100%',
        bgcolor: 'background.paper',
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          borderColor: theme.palette.grey[400],
          boxShadow: `0 8px 16px ${alpha(theme.palette.grey[500], 0.16)}`,
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <Avatar
          sx={{
            bgcolor: alpha(paletteColor.main, 0.2),
            color: paletteColor.dark ?? paletteColor.main,
            width: 48,
            height: 48,
          }}
        >
          <Icon />
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h4" sx={{ color: paletteColor.dark ?? paletteColor.main, lineHeight: 1.2 }}>
            {fNumber(value ?? 0)}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {label}
          </Typography>
        </Box>
      </Stack>
    </Card>
  );
}

KpiCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number,
  color: PropTypes.string.isRequired,
  Icon: PropTypes.elementType.isRequired,
  onClick: PropTypes.func,
};

/**
 * @returns Entity KPI card with name, KPI cards and last-30-days signups chart
 */
export default function AppEntity() {
  const theme = useTheme();
  const navigate = useNavigate();
  const is600Screen = useMediaQuery('(min-width: 600px)');
  const [data, setData] = useState(null);
  const [dataTaken, setDataTaken] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedEntity, setSelectedEntity] = useState(() => {
    try {
      const stored = sessionStorage.getItem('dashboardSelectedEntity');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [openEntitiesDialog, setOpenEntitiesDialog] = useState(false);

  useEffect(() => {
    if (selectedEntity) {
      sessionStorage.setItem('dashboardSelectedEntity', JSON.stringify(selectedEntity));
    } else {
      sessionStorage.removeItem('dashboardSelectedEntity');
    }
  }, [selectedEntity]);

  useEffect(() => {
    setDataTaken(false);
    setErrorMessage('');
    API.get(`/userauth/dashboard-kpi`, {
      params: selectedEntity ? { entity_id: selectedEntity.id } : {},
    })
      .then((response) => {
        setData(response.data?.data ?? response.data);
        setDataTaken(true);
      })
      .catch((error) => {
        setDataTaken(true);
        HttpErrorHandler(error, setErrorMessage);
      });
  }, [selectedEntity]);

  const handleCloseEntitiesDialog = () => setOpenEntitiesDialog(false);
  const getSelectedEntity = (entity) => setSelectedEntity(entity);

  const handleKpiClick = (kpi) => {
    const entity = selectedEntity ?? data?.entity ?? null;

    if (kpi.target === 'children') {
      if (!entity?.id) return;
      navigate('/admin/organization/federations/federation-child', {
        state: { federationID: entity.id },
      });
      return;
    }

    sessionStorage.setItem(
      'userFilters',
      JSON.stringify({
        name: '',
        surname: '',
        email: '',
        phone: '',
        card_number: '',
        fiscal_code: '',
        entity,
        category: kpi.category ?? '',
      })
    );
    sessionStorage.setItem('page', JSON.stringify(0));
    navigate('/admin/users');
  };

  const signups = useMemo(() => data?.signups_last_30_days ?? [], [data]);
  const totalSignups = useMemo(() => signups.reduce((acc, s) => acc + (s.count || 0), 0), [signups]);

  const chartOptions = useChart({
    chart: { type: 'area', toolbar: { show: false }, sparkline: { enabled: false } },
    colors: [theme.palette.primary.main],
    stroke: { curve: 'smooth', width: 3 },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.55,
        opacityTo: 0.05,
        stops: [0, 100],
        colorStops: [
          { offset: 0, color: theme.palette.primary.main, opacity: 0.55 },
          { offset: 100, color: theme.palette.primary.light, opacity: 0.05 },
        ],
      },
    },
    dataLabels: { enabled: false },
    grid: {
      strokeDashArray: 4,
      borderColor: theme.palette.divider,
      yaxis: { lines: { show: true } },
      xaxis: { lines: { show: false } },
    },
    xaxis: {
      categories: signups.map((s) => dayjs(s.date).format('DD/MM')),
      labels: { rotate: -45, style: { fontSize: '11px', colors: theme.palette.text.secondary } },
      tickAmount: is600Screen ? 10 : 5,
      axisBorder: { show: false },
      axisTicks: { show: true },
    },
    yaxis: {
      labels: { formatter: (v) => fNumber(Math.round(v)), style: { colors: theme.palette.text.secondary } },
      min: 0,
      forceNiceScale: true,
    },
    tooltip: {
      x: {
        show: true,
        formatter: (_v, { dataPointIndex }) =>
          signups[dataPointIndex] ? dayjs(signups[dataPointIndex].date).format('DD/MM/YYYY') : '',
      },
      y: { formatter: (v) => `${fNumber(v)} registrazioni` },
      marker: { show: false },
    },
    markers: {
      size: 0,
      hover: { size: 6 },
      strokeColors: theme.palette.background.paper,
      colors: [theme.palette.primary.main],
    },
    legend: { show: false },
  });

  const chartSeries = useMemo(() => [{ name: 'Registrazioni', data: signups.map((s) => s.count) }], [signups]);

  const entityName = selectedEntity?.name ?? data?.entity?.name ?? data?.name ?? '';

  return (
    <Card
      sx={{
        p: { xs: 2, sm: 3 },
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
            }}
          >
            <ApartmentIcon fontSize="large" />
          </Avatar>
          <Box>
            <Typography variant="overline" color="text.secondary">
              Entità
            </Typography>
            <Typography variant="h5" sx={{ lineHeight: 1.2 }}>
              {entityName || '—'}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <Box sx={{ minWidth: 220 }}>
            <CustomTextField
              label="Cambia entità"
              value={dataTaken ? entityName : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ cursor: dataTaken ? 'pointer' : 'default' }}>
                    {dataTaken ? (
                      <Tooltip title="Seleziona entità" placement="top" arrow>
                        <EditIcon onClick={() => setOpenEntitiesDialog(true)} />
                      </Tooltip>
                    ) : (
                      <CircularProgress size={20} color="primary" />
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <DefaultOutlinedButton
            onClick={() => setSelectedEntity(null)}
            disabled={!selectedEntity}
            startIcon={<RestartAltIcon />}
            customStyles={{ height: '56px' }}
          >
            Reimposta
          </DefaultOutlinedButton>
        </Stack>

        <EntitiesDialog
          openDialog={openEntitiesDialog}
          closeDialog={handleCloseEntitiesDialog}
          outputEntity={getSelectedEntity}
          context={'dashboard'}
        />
      </Stack>

      {!dataTaken && <CardContent>{LoadingUI()}</CardContent>}

      {dataTaken && errorMessage?.length !== 0 && ErrorUI(errorMessage)}

      {dataTaken && errorMessage?.length === 0 && data && (
        <Grid container spacing={2.5}>
          {KPI_CONFIG.map((kpi) => (
            <Grid item xs={12} sm={6} md={3} key={kpi.key}>
              <KpiCard
                label={kpi.label}
                value={data?.[kpi.key]}
                color={kpi.color}
                Icon={kpi.icon}
                onClick={() => handleKpiClick(kpi)}
              />
            </Grid>
          ))}

          <Grid item xs={12}>
            <Card
              sx={{
                p: { xs: 2, sm: 3 },
                boxShadow: 'none',
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Ultimi 30 giorni
                  </Typography>
                  <Typography variant="h6">Registrazioni</Typography>
                </Box>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.dark,
                  }}
                >
                  <TrendingUpIcon fontSize="small" />
                  <Typography variant="subtitle2">{fNumber(totalSignups)} totali</Typography>
                </Stack>
              </Stack>
              <Chart type="area" series={chartSeries} options={chartOptions} height={280} />
            </Card>
          </Grid>
        </Grid>
      )}
    </Card>
  );
}
